import axios from 'axios';
import GamesVisAudio from './AudioManager';
import { getApiRoute } from '../../config';
import store from '@/store';
import { isNil } from 'lodash';

const AudioManager = new GamesVisAudio();

/**
 * Return AJS activation status per game/product
 * @param environment [optional] Environment
 * @param productId [mandatory] Game channel - cpvUuid
 * @returns {Boolean}
 */
const isAjsEnabled = ({ productId, environment, platformDataCenter }) => {
  return axios
    .get(`${getApiRoute('ngsEndpoint', environment, platformDataCenter)}/open/ajs/status?gameUuid=${productId}`)
    .then((response) => {
      const responseObj = (!isNil(response) && response.data) || null;

      return responseObj && responseObj.ajsEnabled;
    });
};

/**
 * Gets AJS socket supported events
 * @returns {Object}
 */
const getSupportedEvents = () => {
  return store.getters['ajs/supportedEvents'];
};

/**
 * Returns AJS config
 * @param productId [mandatory] Game channel - cpvUuid
 * @param company [mandatory] Company UUID
 * @param environment [optional] Environment
 * @param deliveryChannel [optional] Delivery channel - retail, terminal, web, mobile
 * @param betshop [optional] Betshop UUID or web/mobile
 * @param location [optional] Device UUID or web/mobile
 * @returns {Object} AJS config
 */
const getJackpotDetails = ({
  productId,
  company,
  environment,
  deliveryChannel,
  betshop = deliveryChannel,
  location = deliveryChannel,
}) => {
  if (isNil(productId) || isNil(company) || isNil(environment) || isNil(deliveryChannel)) return null;

  const deliveryChannels = store.getters['ajs/deliveryChannels'];
  const deliveryChannelMapper = store.getters['ajs/deliveryChannelMapper'];
  const bs = deliveryChannelMapper[betshop] || betshop;
  const lc = deliveryChannelMapper[location] || location;

  return axios
    .get(
      `${getApiRoute('ajsApi', environment)}/open/nonstandalone?account=${company}&game=${productId}&channel=${
        deliveryChannels[deliveryChannel]
      }&betshop=${bs}&location=${lc}`,
    )
    .then((res) => {
      const resObj = res.data;

      if (!isNil(resObj) && !isNil(resObj.jackpotId) && !isNil(resObj.funds)) {
        // console.log('[V-SDK] getJackpotDetails AJS success!', resObj);
        return resObj;
      }

      console.log('[V-SDK] getJackpotDetails AJS Fail!', resObj);
      return null;
    });
};

/**
 * Checks AJS activation, gets config and integrates it to store
 * @param productId [mandatory] Game channel - cpvUuid
 * @param company [mandatory] Company UUID
 * @param environment [optional] Environment
 * @param deliveryChannel [optional] Delivery channel - retail, terminal, web, mobile
 * @param betshop [optional] Betshop UUID or web/mobile
 * @param location [optional] Device UUID or web/mobile
 * @param cmsConfig [optional] Company CMS config
 * @param locale [optional] Desired language
 * @returns {Object} AJS config
 */
const initializeAjs = ({
  productId,
  company,
  environment,
  platformDataCenter,
  deliveryChannel,
  betshop = deliveryChannel,
  location = deliveryChannel,
  cmsConfig = {},
  locale = 'en',
}) => {
  if (isNil(productId) || isNil(company) || isNil(environment) || isNil(deliveryChannel)) return null;

  const deliveryChannelMapper = store.getters['ajs/deliveryChannelMapper'];

  return isAjsEnabled({ productId, environment, platformDataCenter }).then((response) => {
    const ajsEnabled = response;

    const bs = deliveryChannelMapper[betshop] || betshop;
    const lc = deliveryChannelMapper[location] || location;

    if (ajsEnabled) {
      // Set general settings received through arguments
      store.dispatch('ajs/setGeneralConfig', {
        productId,
        company,
        environment,
        deliveryChannel,
        cmsConfig,
        locale,
        betshop: bs,
        location: lc,
      });

      return getJackpotDetails({
        productId,
        company,
        environment,
        deliveryChannel,
        betshop,
        location,
      }).then((res) => {
        const resObj = res;

        if ((!isNil(resObj) && !isNil(resObj.jackpotId) && !isNil(resObj.funds)) || store.getters['ajs/ajsActive']) {
          // console.log('[V-SDK] AJS integration proceed!', resObj);
          // Set complete AJS settings to store
          store.dispatch('ajs/setAjsConfig', resObj);
          return resObj;
        }

        console.log('[V-SDK] integrate AJS Fail!', resObj, {
          productId,
          company,
          environment,
          platformDataCenter,
          deliveryChannel,
          betshop,
          location,
          cmsConfig,
          locale,
        });

        return null;
      });
    }
    return null;
  });
};

const updateAjs = () => {
  const { productId, company, environment, deliveryChannel, betshop, location } = store.state.ajs;

  getJackpotDetails({
    productId,
    company,
    environment,
    deliveryChannel,
    betshop,
    location,
  }).then((res) => {
    const resObj = res;

    if ((!isNil(resObj) && !isNil(resObj.jackpotId) && !isNil(resObj.funds)) || store.getters['ajs/ajsActive']) {
      store.dispatch('ajs/updateAjsConfig', resObj);
    }
  });
};

export default {
  AudioManager,
  isAjsEnabled,
  initializeAjs,
  updateAjs,
  getJackpotDetails,
  getSupportedEvents,
};
